import { useMemo } from 'react';

type Query = Record<string, unknown>;

import type { NativeReportResponse } from '@/api/reports';
import { useOrg } from '@/contexts/OrgContext';
import { useSchemaLabels } from '@/lib/crm/hooks';
import { ChartRenderer } from '@/pages/reports/ChartRenderer';
import { deriveDimensionNamesFromColumns } from '@/pages/reports/utils/dimensionNames';

import type {
  ChartProps,
  ComponentRenderProps,
} from '../../../types/dynamic_views';

/** stub kept for ChartRenderer's optional query prop */
const NATIVE_RENDER_QUERY_STUB: Query = {};

/**
 * Renders a `chart` manifest component as a Recharts visualization.
 *
 * Delegates to the existing `ChartRenderer` in `pages/reports/` with the
 * `nativeData` prop — the same path the saved-report wizard uses. We
 * convert the manifest's column descriptors + row dicts into the
 * `NativeReportResponse` wire format and hand off.
 *
 * Used by the chat surface to render `query_report` and
 * `execute_saved_report` tool results inline once the backend wraps them
 * in a manifest envelope.
 */
export const ChartComponent: React.FC<ComponentRenderProps> = ({
  component,
  className = '',
}) => {
  // Runtime fallback: a malformed manifest (missing `props`, or with `props`
  // set to a non-object) would crash destructuring below before any of the
  // validation UI renders. Treat that case the same as "no columns".
  const rawProps = component.props;
  const props =
    rawProps && typeof rawProps === 'object'
      ? (rawProps as ChartProps)
      : ({} as ChartProps);
  const { chart_type, columns, rows, title } = props;

  // Stable reference: keep nativeData consistent across renders that don't
  // change the underlying props, so ChartRenderer's internal memoization
  // works correctly.
  const nativeData = useMemo<NativeReportResponse>(
    () => ({
      columns: columns ?? [],
      rows: rows ?? [],
      total_count: rows?.length ?? 0,
    }),
    [columns, rows]
  );

  const { currentOrg } = useOrg();
  const { resolveLabel } = useSchemaLabels(currentOrg?.id);
  const dimensionNames = useMemo(
    () => deriveDimensionNamesFromColumns(nativeData.columns ?? []),
    [nativeData]
  );

  if (!columns || !Array.isArray(columns) || columns.length === 0) {
    return (
      <div
        className='rounded p-4'
        style={{
          backgroundColor: 'var(--color-bg-error-secondary)',
          color: 'var(--color-text-error-primary)',
        }}
      >
        <div className='text-[13px] font-medium'>Invalid chart data</div>
        <div className='mt-1 text-[12px]'>
          Columns must be a non-empty array.
        </div>
      </div>
    );
  }

  if (!rows || !Array.isArray(rows)) {
    return (
      <div
        className='rounded p-4'
        style={{
          backgroundColor: 'var(--color-bg-warning-secondary)',
          color: 'var(--color-text-warning-primary)',
        }}
      >
        <div className='text-[13px] font-medium'>No chart data</div>
        <div className='mt-1 text-[12px]'>No rows provided for the chart.</div>
      </div>
    );
  }

  return (
    <div className={className} data-component-id={component.id}>
      {title && (
        <div className='mb-2 text-[14px] font-medium text-fg-primary'>
          {title}
        </div>
      )}
      <ChartRenderer
        query={NATIVE_RENDER_QUERY_STUB}
        chartType={chart_type}
        nativeData={nativeData}
        nativeChartType={chart_type}
        isLoading={false}
        resolveLabel={resolveLabel}
        dimensionName={dimensionNames[0]}
        dimensionNames={dimensionNames}
      />
    </div>
  );
};

export default ChartComponent;
